import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader, Button, Input, Select, SelectTrigger, SelectValue, SelectContent, SelectItem, AlertDialog } from '@/components/shared/ui';
import { ToggleLabel } from '@/components/shared/ui/Toggle';
import { Plus, Upload } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES } from '../../lib/constants/routes';
import { useOrganizationTitleList } from '../../hooks/useOrganization';
import CurriculumSetup from '../product/CurriculumSetup';
import AddScenarioModal from '../product/AddScenarioModal';
import { CurriculumItem, VideoLecture } from '../../types/curriculum';
import type { ScenarioDetail } from '../../data/scenarioDetails';
import type { ScenarioAdminDto } from '@/types/api/scenario';
import type { CompletionRequirement } from '../product/types';
import { useCreateClass, useOrderUserCount } from '../../data/queries/useClasses';
import { useProductList, useClassFormProductDetail, useOrganizationPurchasedProducts } from '../../hooks/useProduct';
import { useOrderDetail } from '../../hooks/useOrder';
import { isContentLikeProductType, isInstitutionClassProductSelectType } from '@/utils/productUtils';
import { buildClassPriceSelectOptions } from '@/lib/classPriceSelectOptions';
import { formatSalesUnitMoneyCell } from '@/lib/productSalesUnitTableDisplay';
import { mapPurchasedProductListToSelectItems } from '@/services/productService';
import type { ProductListItemDto } from '@/types/api/product';
import type { ClassItemInfoDto } from '@/types/api/class';
import { findPurchasedRowForProduct, resolvePurchasedProductPriceId } from '@/lib/resolvePurchasedProductPriceId';
import { pickClassItemExpectedPlaySeconds } from '@/utils/classUtils';
import { uploadFileViaPresignedUrl } from '@/services/fileService';
import { ApiError } from '@/services/apiClient';
import { useTranslationWithLoading } from '@/hooks/useTranslationWithLoading';
import {
    buildClassTranslationTriple,
    coalesceEmptyTitleDescSlots,
    createEmptyTitleDescTriple,
    translateTitleDescTripleIfNeeded,
    type TitleDescTriple,
} from '@/lib/apiTranslations';
import { resolveTripleLocaleForEntityForm } from '@/lib/localeTripleUi';

/** 기관 클래스 생성 API 목록: 레포 `docs/class-create-org-api.md` 참고 */

function durationLabelFromPlaySeconds(sec: number | undefined): string {
	if (sec == null || sec <= 0) return '0분';
	return `${Math.max(1, Math.ceil(sec / 60))}분`;
}

/** CreateClassRequestDto: maxParticipants 0 means unlimited. */
const UNLIMITED_MAX_PARTICIPANTS = 0;

function resolveOrderAvailableCapacity(
    data: { totalQuantity: number | null; usedCapacity: number | null; availableCapacity: number | null } | undefined,
    fallbackTotalQuantity?: number | null
): number | null | undefined {
    if (data == null) return undefined;
    if (typeof data.availableCapacity === 'number' && Number.isFinite(data.availableCapacity)) {
        return data.availableCapacity;
    }
    const totalQuantity =
        typeof data.totalQuantity === 'number' && Number.isFinite(data.totalQuantity)
            ? data.totalQuantity
            : typeof fallbackTotalQuantity === 'number' && Number.isFinite(fallbackTotalQuantity)
              ? fallbackTotalQuantity
              : null;
    if (totalQuantity != null) {
        const used = typeof data.usedCapacity === 'number' && Number.isFinite(data.usedCapacity)
            ? data.usedCapacity
            : 0;
        return Math.max(0, totalQuantity - used);
    }
    return null;
}

function getOrderDetailQuantityForProductPrice(
    order: { items?: Array<{ productPriceId?: number; quantity?: number }> } | null | undefined,
    productPriceId: number
): number | null {
    if (!order?.items?.length || productPriceId <= 0) return null;
    let total = 0;
    for (const item of order.items) {
        if (Number(item.productPriceId) !== productPriceId) continue;
        const quantity = Number(item.quantity);
        if (Number.isFinite(quantity) && quantity > 0) total += quantity;
    }
    return total > 0 ? total : null;
}

function clampParticipantsInput(raw: string, availableCapacity: number | null | undefined): number {
    const parsed = parseInt(raw, 10) || 0;
    if (availableCapacity != null && availableCapacity >= 0) {
        return Math.min(parsed, availableCapacity);
    }
    return parsed;
}

/**
 * 수강기간 종료일과 프로덕트 유효기간으로 모집기간 최대 종료일 계산
 * @param courseEndDate 수강기간 종료일 (YYYY-MM-DD)
 * @param validityPeriod 유효기간 (숫자)
 * @param validityPeriodUnit 유효기간 단위 (DAY, MONTH, YEAR)
 * @returns 모집기간 최대 종료일 (YYYY-MM-DD) 또는 null
 */
function calculateMaxRecruitmentEndDate(
    courseEndDate: string | undefined,
    validityPeriod: number | null | undefined,
    validityPeriodUnit: string | null | undefined
): string | null {
    if (!courseEndDate || validityPeriod == null || validityPeriod <= 0) return null;
    
    const endDate = new Date(courseEndDate);
    if (isNaN(endDate.getTime())) return null;
    
    // validityPeriod만큼 빼기
    if (validityPeriodUnit === 'DAY') {
        endDate.setDate(endDate.getDate() - validityPeriod);
    } else if (validityPeriodUnit === 'MONTH') {
        endDate.setMonth(endDate.getMonth() - validityPeriod);
    } else if (validityPeriodUnit === 'YEAR') {
        endDate.setFullYear(endDate.getFullYear() - validityPeriod);
    } else {
        return null;
    }
    
    return endDate.toISOString().split('T')[0];
}

export default function ClassCreate(): React.ReactElement {
    const { t, i18n } = useTranslation('class');
    const navigate = useNavigate();
    const location = useLocation();
    const { getCurrentRole } = useAuth();
    const isMaster = getCurrentRole() === 'master';
    /** `/admin/open-class/create` · `/master/open-class/create` — 어드민·마스터 공통 오픈클래스 생성 폼 */
    const isOpenClassPath = location.pathname.includes('/open-class/create');
    /** 어드민 > 클래스관리 > 기관클래스 생성 — 주문에 없는 프로덕트는 선택지에서 제외 */
    const isAdminOrgClassCreate = location.pathname.includes('/admin/class/create');
    /** 어드민·마스터 기관클래스 생성: 프로덕트 목록은 GET /product/organization/purchased 만 사용 (GET /product/list 폴백 없음) */
    const isOrgClassCreatePath =
        location.pathname.includes('/admin/class/create') || location.pathname.includes('/master/class/create');
    const pageTitle = isOpenClassPath ? t('create.pageTitleOpen') : t('create.pageTitleOrg');
    type ClassCreateLocationState = {
        orderId?: number;
        organizationId?: string | number;
    };
    const locState = (location.state as ClassCreateLocationState | null) ?? null;
    const orderIdFromState = locState?.orderId;
    const organizationIdFromState = locState?.organizationId;

    const [organizationId, setOrganizationId] = useState<string>(
        organizationIdFromState != null ? String(organizationIdFromState) : ''
    );
    const [organizationSearchText, setOrganizationSearchText] = useState('');
    const [selectedProductId, setSelectedProductId] = useState<number>(0);
    const [selectedProductPriceId, setSelectedProductPriceId] = useState<number>(0);
    const [selectedSalesUnitId, setSelectedSalesUnitId] = useState<number>(0);

    // 기관 목록 (selectedOrg·기관 ID 계산)
    const { data: organizationsData } = useOrganizationTitleList();
    const organizationList = organizationsData?.organizations ?? [];
    /** 오픈클래스: isPartner === true 인 기관만 */
    const { data: partnerOrganizationsData, isLoading: isPartnerLoading } = useOrganizationTitleList(
        { isPartner: true },
        { enabled: isOpenClassPath }
    );
    const partnerOrganizationList = partnerOrganizationsData?.organizations ?? [];

    // 선택된 기관 정보 (기관명으로 주문 검색에 사용)
    const selectedOrg = useMemo(
        () => organizationList.find((o) => String(o.id) === organizationId),
        [organizationList, organizationId]
    );

    // 검색어로 필터링된 기관 목록
    const filteredOrganizationList = useMemo(() => {
        if (!organizationSearchText.trim()) return organizationList;
        const searchLower = organizationSearchText.toLowerCase();
        return organizationList.filter((org) =>
            org.title.toLowerCase().includes(searchLower)
        );
    }, [organizationList, organizationSearchText]);

    const selectedOrgNumericId = Number(organizationId);

    /** 마스터 + 기관 선택됨(기관클래스 생성) — 주문·가격 행 분기 */
    const masterOrgClassContext =
        isMaster && !isOpenClassPath && Number.isFinite(selectedOrgNumericId) && selectedOrgNumericId > 0;
    const usePurchasedProductsForClass =
        isOrgClassCreatePath && Number.isFinite(selectedOrgNumericId) && selectedOrgNumericId > 0;

    // 어드민·마스터 기관클래스: GET /product/organization/purchased?organizationId=&page=&pageSize=
    const { data: purchasedProductsData, isFetching: purchasedProductsFetching } =
        useOrganizationPurchasedProducts(selectedOrgNumericId, {
            enabled:
                usePurchasedProductsForClass &&
                Number.isFinite(selectedOrgNumericId) &&
                selectedOrgNumericId > 0,
        });

    const purchasedList = purchasedProductsData?.productList ?? [];
    const purchasedListFetchDone =
        usePurchasedProductsForClass &&
        Number.isFinite(selectedOrgNumericId) &&
        selectedOrgNumericId > 0 &&
        !purchasedProductsFetching &&
        purchasedProductsData !== undefined;

    /** 오픈클래스만 GET /product/list — 어드민·마스터 기관클래스 생성은 GET /product/organization/purchased 만 사용 */
    const { data: productListData } = useProductList(
        { page: 1, pageSize: 500 },
        {
            enabled: isOpenClassPath,
        }
    );

    const purchasedSelectItems = useMemo(
        () =>
            mapPurchasedProductListToSelectItems(purchasedList, undefined, {
                institutionClassProductSelect: isOrgClassCreatePath,
            }),
        [purchasedList, isOrgClassCreatePath]
    );

    /**
     * 기관클래스: GET /product/organization/purchased 한 행 — 여기 orderId(주문)가 백엔드 클래스 생성·주문기반 조회에 필수
     * productPriceId로 직접 선택된 행 찾기
     */
    const purchasedRowForClassForm = useMemo(() => {
        if (!isOrgClassCreatePath || selectedProductPriceId <= 0) return undefined;
        return purchasedList.find((r) => r.productPriceId === selectedProductPriceId);
    }, [isOrgClassCreatePath, selectedProductPriceId, purchasedList]);

    // 선택된 productId 설정 (purchasedRowForClassForm에서 가져옴)
    useEffect(() => {
        if (purchasedRowForClassForm?.productId) {
            setSelectedProductId(purchasedRowForClassForm.productId);
        }
    }, [purchasedRowForClassForm]);

    const productOptions = useMemo(() => {
        const filterContentActive = (list: ProductListItemDto[] | undefined) =>
            (list ?? []).filter(
                (p) => isContentLikeProductType(p.type) && p.isActive !== false
            );
        const filterInstitutionClassSelectable = (list: ProductListItemDto[] | undefined) =>
            (list ?? []).filter(
                (p) => isInstitutionClassProductSelectType(p.type) && p.isActive !== false
            );

        if (isOrgClassCreatePath) {
            if (!Number.isFinite(selectedOrgNumericId) || selectedOrgNumericId <= 0) return [];
            return filterInstitutionClassSelectable(purchasedSelectItems);
        }

        return filterContentActive(productListData?.productList);
    }, [
        productListData,
        purchasedSelectItems,
        isOrgClassCreatePath,
        selectedOrgNumericId,
    ]);

    /**
     * 주문 id: 기관클래스는 GET /product/organization/purchased 의 해당 행 orderId 필수.
     * 오픈클래스는 (진입 state 등) state.orderId만 쓰는 기존 동작 유지.
     */
    const resolvedOrderId = useMemo(() => {
        if (isOpenClassPath) {
            if (orderIdFromState != null) {
                const n = Number(orderIdFromState);
                if (Number.isFinite(n) && n > 0) {
                    return n;
                }
            }
            return undefined;
        }
        if (isOrgClassCreatePath) {
            const fromRow = purchasedRowForClassForm?.orderId;
            if (fromRow != null && Number(fromRow) > 0) {
                return Math.floor(Number(fromRow));
            }
            if (orderIdFromState != null) {
                const n = Number(orderIdFromState);
                if (Number.isFinite(n) && n > 0) {
                    return n;
                }
            }
            return undefined;
        }
        return undefined;
    }, [isOpenClassPath, isOrgClassCreatePath, purchasedRowForClassForm?.orderId, orderIdFromState]);

    const selectedProductMeta = useMemo(() => {
        if (isOrgClassCreatePath && purchasedRowForClassForm) {
            return { 
                title: purchasedRowForClassForm.productTitle ?? '', 
                type: purchasedRowForClassForm.type ?? 'CONTENT' 
            };
        }
        const p = productOptions.find((x) => x.productId === selectedProductId);
        return { title: p?.title ?? '', type: p?.type };
    }, [isOrgClassCreatePath, purchasedRowForClassForm, productOptions, selectedProductId]);

    const classFormProductDetailEnabled =
        selectedProductId > 0 &&
        (!isOrgClassCreatePath || (purchasedListFetchDone && purchasedRowForClassForm != null));

    const { data: productDetail, isLoading: productDetailLoading } = useClassFormProductDetail(selectedProductId, {
        titleFallback: selectedProductMeta.title,
        typeFallback: selectedProductMeta.type,
        purchasedPriceRow: purchasedRowForClassForm,
        enabled: classFormProductDetailEnabled,
        staleTime: 0,
        refetchOnMount: 'always',
    });

    /** 마스터 기관클래스: 판매단위 UI 없이 상세/목록의 첫 활성 판매단위 사용 */
    const masterAutoProductPriceId = useMemo(() => {
        if (!isMaster || selectedProductId <= 0) return 0;
        const prices = productDetail?.prices ?? [];
        if (prices.length === 0) return 0;
        const preferred = prices.find((p) => p.isActive !== false) ?? prices[0];
        return preferred?.productPriceId ?? 0;
    }, [isMaster, selectedProductId, productDetail?.prices]);

    const effectiveProductPriceId = isOrgClassCreatePath
        ? selectedProductPriceId
        : isMaster
          ? masterAutoProductPriceId
          : selectedSalesUnitId;

    const priceOptions = useMemo(
        () =>
            buildClassPriceSelectOptions(
                productDetail?.prices,
                isMaster
                    ? masterAutoProductPriceId
                    : selectedSalesUnitId,
                t('edit.salesUnitLegacyOption')
            ),
        [productDetail?.prices, isMaster, masterAutoProductPriceId, selectedSalesUnitId, t]
    );

    const [classI18n, setClassI18n] = useState<TitleDescTriple>(() => createEmptyTitleDescTriple());
    const formLocale = useMemo(() => resolveTripleLocaleForEntityForm(i18n.language), [i18n.language]);
    const [thumbnail, setThumbnail] = useState<string>('');
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [curriculumItems, setCurriculumItems] = useState<CurriculumItem[]>([]);
    const [completionRequirements, setCompletionRequirements] = useState<CompletionRequirement>({
        minScenarioMode: 'all',
        minScenarios: 1,
        minPassingScore: 70,
    });
    const [useCompletionRequirements, setUseCompletionRequirements] = useState(false);
    const [scenarioModalOpen, setScenarioModalOpen] = useState(false);

    const todayYmd = useMemo(() => {
        const now = new Date();
        const y = now.getFullYear();
        const m = String(now.getMonth() + 1).padStart(2, '0');
        const d = String(now.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }, []);

    const [participationMethod, setParticipationMethod] = useState<'online' | 'inPerson'>('online');
    const [startDate, setStartDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [recruitmentStartDate, setRecruitmentStartDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [recruitmentEndDate, setRecruitmentEndDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [maxParticipants, setMaxParticipants] = useState<number>(30);
    const [partnerOrgId, setPartnerOrgId] = useState<string>('');
    const [isActive, setIsActive] = useState(true);

    // 선택된 프로덕트의 주문 만료일 (수강 종료일 제한용)
    const productExpiresAt = useMemo(() => {
        if (!purchasedRowForClassForm?.expiresAt) return null;
        const date = new Date(purchasedRowForClassForm.expiresAt);
        return date.toISOString().split('T')[0]; // YYYY-MM-DD 형식
    }, [purchasedRowForClassForm]);
    
    // 선택된 프로덕트의 주문 시작일 (수강 시작일 최소값 제한용)
    const productStartAt = useMemo(() => {
        if (!purchasedRowForClassForm?.startAt) return null;
        const date = new Date(purchasedRowForClassForm.startAt);
        return date.toISOString().split('T')[0]; // YYYY-MM-DD 형식
    }, [purchasedRowForClassForm]);
    
    // 대면 클래스: 모집기간 최대 종료일 계산 (수강기간 종료일 - 프로덕트 유효기간)
    const maxRecruitmentEndDate = useMemo(() => {
        if (participationMethod !== 'inPerson' || !endDate) return endDate || undefined;
        
        const selectedPrice = priceOptions.find((p) => p.productPriceId === selectedSalesUnitId);
        if (!selectedPrice) return endDate || undefined;
        
        const calculatedMax = calculateMaxRecruitmentEndDate(
            endDate,
            selectedPrice.validityPeriod,
            selectedPrice.validityPeriodUnit
        );
        
        return calculatedMax || endDate || undefined;
    }, [participationMethod, endDate, priceOptions, selectedSalesUnitId]);

    // 파트너 목록이 바뀌면 잘못된 선택 제거
    useEffect(() => {
        if (!partnerOrgId) return;
        const stillValid = partnerOrganizationList.some((o) => String(o.id) === partnerOrgId);
        if (!stillValid) setPartnerOrgId('');
    }, [partnerOrganizationList, partnerOrgId]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMsg, setAlertMsg] = useState('');
    const [navigateOnClose, setNavigateOnClose] = useState(false);
    const showParticipantLimit = !isOpenClassPath || participationMethod === 'inPerson';
    const { data: orderUserCountData } = useOrderUserCount(
        resolvedOrderId,
        effectiveProductPriceId > 0 ? effectiveProductPriceId : undefined,
        { enabled: !!resolvedOrderId && effectiveProductPriceId > 0 && showParticipantLimit }
    );

    /** 기관 클래스 생성(어드민·마스터): 수강기간 자동값에 쓰일 주문 상세(GET /order/{id}) */
    const shouldFetchOrderDetailForClassPeriod =
        !!resolvedOrderId &&
        resolvedOrderId > 0 &&
        (isAdminOrgClassCreate || (isMaster && !isOpenClassPath));

    // 선택된 주문 상세 조회 — 사용시작일(startAt), 만료일(expiresAt) 추출
    const { data: selectedOrderDetail } = useOrderDetail(
        resolvedOrderId ?? 0,
        { enabled: shouldFetchOrderDetailForClassPeriod, staleTime: 30000 }
    );
    const orderDetailQuantityForSelectedPrice = getOrderDetailQuantityForProductPrice(
        selectedOrderDetail,
        effectiveProductPriceId
    );
    const resolvedAvailableCapacity = resolveOrderAvailableCapacity(
        orderUserCountData,
        orderDetailQuantityForSelectedPrice
    );
    const hasUnlimitedOrderCapacity =
        showParticipantLimit &&
        orderUserCountData !== undefined &&
        selectedOrderDetail !== undefined &&
        resolvedAvailableCapacity == null;

    const createMutation = useCreateClass();
    const { withTranslationLoading } = useTranslationWithLoading();

    const selectedScenarioIds = useMemo(() => curriculumItems
        .filter((i): i is CurriculumItem & { type: 'scenario' } => i?.type === 'scenario')
        .map((i) => {
            const d = i.data as ScenarioDetail & { scenarioId?: number };
            return d.scenarioId ?? d.id;
        })
        .filter((id): id is number => id != null), [curriculumItems]);

    // 기관 변경 시 프로덕트/판매단위 초기화
    useEffect(() => {
        if (!isOpenClassPath && organizationId) {
            setSelectedProductId(0);
            setSelectedProductPriceId(0);
            setSelectedSalesUnitId(0);
        }
    }, [isOpenClassPath, organizationId]);

    // 필터 결과에서 제외된 프로덕트가 선택된 경우 선택값 초기화
    useEffect(() => {
        if (isOrgClassCreatePath) {
            if (selectedProductPriceId <= 0) return;
            const exists = purchasedList.some((r) => r.productPriceId === selectedProductPriceId);
            if (!exists) {
                setSelectedProductPriceId(0);
                setSelectedProductId(0);
            }
        } else {
            if (selectedProductId <= 0) return;
            const exists = productOptions.some((p) => p.productId === selectedProductId);
            if (!exists) {
                setSelectedProductId(0);
                setSelectedSalesUnitId(0);
            }
        }
    }, [isOrgClassCreatePath, selectedProductPriceId, selectedProductId, purchasedList, productOptions]);

    // 주문 상세 로드 후 수강기간 자동 설정 (기관클래스: 어드민·마스터 동일)
    // 시작: 주문 사용시작일(startAt), 과거면 오늘 / 종료: 프로덕트 사용종료일(expiresAt), 없으면 시작일과 맞춰 이전 주문 값 잔존 방지
    useEffect(() => {
        if (!shouldFetchOrderDetailForClassPeriod || !selectedOrderDetail) return;
        const today = new Date().toISOString().split('T')[0];

        const rawStart = selectedOrderDetail.startAt;
        let startYmd = today;
        if (rawStart) {
            const s = rawStart.slice(0, 10);
            startYmd = s < today ? today : s;
        }
        setStartDate(startYmd);

        const rawEnd = selectedOrderDetail.expiresAt;
        if (rawEnd) {
            const endYmd = rawEnd.slice(0, 10);
            setEndDate(endYmd < startYmd ? startYmd : endYmd);
        } else {
            setEndDate(startYmd);
        }
    }, [
        shouldFetchOrderDetailForClassPeriod,
        selectedOrderDetail?.orderId,
        selectedOrderDetail?.startAt,
        selectedOrderDetail?.expiresAt,
    ]);

    // orderUserCountData 로드 후 참가인원(maxParticipants) 자동 설정 — 주문·선택 판매단위 잔여와 맞춤
    // - 기관클래스(어드민·마스터): PER_USER → availableCapacity / PER_DEVICE(null) → 0(무제한)
    // - 오픈클래스 대면: 기존엔 기관만 반영되어 정원이 주문과 어긋날 수 있어 동일 규칙 적용
    useEffect(() => {
        const orgClassLike = isAdminOrgClassCreate || (isMaster && !isOpenClassPath);
        const openInPerson = isOpenClassPath && participationMethod === 'inPerson';
        if ((!orgClassLike && !openInPerson) || !orderUserCountData) return;
        const capacity = resolveOrderAvailableCapacity(orderUserCountData, orderDetailQuantityForSelectedPrice);
        if (capacity != null) {
            setMaxParticipants(capacity);
        } else {
            setMaxParticipants(UNLIMITED_MAX_PARTICIPANTS);
        }
    }, [
        isAdminOrgClassCreate,
        isMaster,
        isOpenClassPath,
        participationMethod,
        orderUserCountData,
        orderDetailQuantityForSelectedPrice,
    ]);

    // 프로덕트 선택 시: 클래스명/설명·커리큘럼 자동 채움 (오픈/마스터 등 판매단위 직접 선택 경로만 초기화)
    useEffect(() => {
        if (!isAdminOrgClassCreate) setSelectedSalesUnitId(0);
        if (!productDetail) return;
        setClassI18n({
            ...createEmptyTitleDescTriple(),
            ko: { title: productDetail.title ?? '', description: productDetail.description ?? '', offlineClassDescription: '' },
        });
        const items: CurriculumItem[] = (productDetail.items ?? []).map((item, idx) => {
            const itemId = `load-${item.scenarioId ?? item.lectureId ?? idx}-${Date.now()}-${idx}`;
            const itemRec = item as unknown as ClassItemInfoDto & Record<string, unknown>;
            const playSec = pickClassItemExpectedPlaySeconds(itemRec) ?? 0;
            if (item.itemType === 'LECTURE') {
                return {
                    id: itemId,
                    type: 'video' as const,
                    data: {
                        id: String(item.lectureId ?? idx),
                        title: item.lectureTitle ?? item.title ?? '',
                        url: item.lectureVideoUrl ?? '',
                        duration: durationLabelFromPlaySeconds(playSec > 0 ? playSec : undefined),
                        author: { name: item.lectureCreatorName ?? '', type: 'individual' as const },
                        description: item.lectureDescription ?? '',
                    } as unknown as VideoLecture,
                };
            }
            return {
                id: itemId,
                type: 'scenario' as const,
                data: {
                    scenarioId: item.scenarioId ?? 0,
                    title: item.title ?? '',
                    expectedPlayTimeSeconds: playSec,
                    duration: durationLabelFromPlaySeconds(playSec > 0 ? playSec : undefined),
                    platform: item.deviceType ?? 'VR',
                } as unknown as ScenarioAdminDto,
            };
        });
        setCurriculumItems(items);
    }, [productDetail, isAdminOrgClassCreate]);

    const handleAddScenariosFromModal = (scenarios: ScenarioDetail[]) => {
        if (!scenarios?.length) return;
        const existingIds = new Set(selectedScenarioIds);
        const toAdd = scenarios.filter((d) => !existingIds.has(d.id));
        if (!toAdd.length) return;
        const newItems: CurriculumItem[] = toAdd.map((detail) => ({
            id: `scenario-${detail.id}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            type: 'scenario' as const,
            data: detail,
        }));
        setCurriculumItems((prev) => [...prev, ...newItems]);
        setScenarioModalOpen(false);
    };


    // 마스터는 기관이 1개이므로 유일한 기관을 자동 설정
    useEffect(() => {
        if (isMaster && organizationList.length >= 1 && !organizationId) {
            setOrganizationId(String(organizationList[0].id));
        }
    }, [isMaster, organizationList, organizationId]);

    const validate = (): boolean => {
        const next: Record<string, string> = {};
        if (!isMaster && !isOpenClassPath && !organizationId) next.organizationId = t('create.errorOrganization');
        // 프로덕트·판매단위 필수 (서버 required)
        if (!selectedProductId || selectedProductId <= 0) next.productId = '프로덕트를 선택해주세요.';
        if (usePurchasedProductsForClass && purchasedListFetchDone && purchasedList.length === 0) {
            next.purchaseProduct =
                '해당 기관으로 구매된 프로덕트가 없습니다. GET /product/organization/purchased 기준입니다. 주문·라이선스를 확인해 주세요.';
        } else if (
            isOrgClassCreatePath &&
            !isOpenClassPath &&
            purchasedListFetchDone &&
            selectedProductPriceId > 0 &&
            (!purchasedRowForClassForm?.orderId || Number(purchasedRowForClassForm.orderId) <= 0)
        ) {
            next.purchaseProduct = t('create.errorOrderId');
        } else if (isOrgClassCreatePath) {
            if (!selectedProductPriceId || selectedProductPriceId <= 0) {
                next.purchaseProduct =
                    '구매 프로덕트에 연결된 가격(productPrice) 정보가 없습니다. 목록을 새로고침하거나 담당자에게 문의해 주세요.';
            }
        } else if (isMaster) {
            const masterPrice = masterAutoProductPriceId;
            if (!masterPrice || masterPrice <= 0) {
                next.salesUnit =
                    '구매 정보 또는 프로덕트에서 판매단위(productPriceId)를 확인할 수 없습니다. 잠시 후 다시 시도해 주세요.';
            }
        } else if (!selectedSalesUnitId || selectedSalesUnitId <= 0) {
            next.salesUnit = '판매단위를 선택해주세요.';
        }
        // 클래스명 필수
        const hasClassTitle = (['ko', 'en', 'ar', 'id', 'mn'] as const).some((c) => classI18n[c].title.trim());
        if (!hasClassTitle) next.className = t('create.errorClassName');
        // 커리큘럼 항목 필수
        if (curriculumItems.length === 0) next.curriculum = '커리큘럼 항목을 1개 이상 추가해주세요.';
        // 수강기간 검증: 기관클래스 or 오픈클래스 대면인 경우만
        if (showParticipantLimit) {
            if (!startDate || !endDate) {
                next.endDate = t('create.errorEndDateAfter');
            } else if (new Date(startDate) > new Date(endDate)) {
                next.endDate = t('create.errorEndDateAfter');
            }
            if (resolvedAvailableCapacity === 0) {
                next.maxParticipants = '등록 가능한 사용자 수가 없습니다.';
            } else if (!hasUnlimitedOrderCapacity && (!Number.isFinite(maxParticipants) || maxParticipants < 1)) {
                next.maxParticipants = t('edit.errorMaxParticipants');
            } else if (resolvedAvailableCapacity != null && maxParticipants > resolvedAvailableCapacity) {
                next.maxParticipants = `참가인원은 등록 가능 사용자 수(${resolvedAvailableCapacity}${t('edit.peopleUnit')})를 초과할 수 없습니다.`;
            }
        }
        // 모집기간·참여인원 검증: 오픈클래스 대면인 경우만
        if (isOpenClassPath && participationMethod === 'inPerson') {
            if (!recruitmentStartDate || !recruitmentEndDate) {
                next.recruitmentEndDate = t('create.errorRecruitmentEndDateAfter');
            } else if (new Date(recruitmentStartDate) > new Date(recruitmentEndDate)) {
                next.recruitmentEndDate = t('create.errorRecruitmentEndDateAfter');
            }
            if (resolvedAvailableCapacity === 0) {
                next.maxParticipants = '등록 가능한 사용자 수가 없습니다.';
            } else if (!hasUnlimitedOrderCapacity && (!Number.isFinite(maxParticipants) || maxParticipants < 1)) {
                next.maxParticipants = t('edit.errorMaxParticipants');
            } else if (resolvedAvailableCapacity != null && maxParticipants > resolvedAvailableCapacity) {
                next.maxParticipants = `참가인원은 등록 가능 사용자 수(${resolvedAvailableCapacity}${t('edit.peopleUnit')})를 초과할 수 없습니다.`;
            }
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const goToClassList = () => {
        if (isMaster) {
            navigate(ROUTES.MASTER.CLASS_MANAGEMENT);
        } else {
            navigate(ROUTES.ADMIN.CLASS_MANAGEMENT, { replace: true });
        }
    };

    const showAlert = (msg: string, goBack = false) => {
        setAlertMsg(msg);
        setNavigateOnClose(goBack);
        setAlertOpen(true);
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        let thumbnailImageUrl: string | undefined;
        if (uploadedFile) {
            try {
                thumbnailImageUrl = await uploadFileViaPresignedUrl(uploadedFile, 'EDU');
            } catch (e) {
                showAlert(e instanceof Error ? e.message : '썸네일 업로드에 실패했습니다.');
                return;
            }
        } else if (thumbnail.trim().startsWith('http')) {
            thumbnailImageUrl = thumbnail.trim();
        }

        // organizationId는 기관클래스일 때만 전송
        // - 마스터: 폼 state(자동 설정·진입 state)의 기관 ID 우선 — 주문 조회·GET /order/organization 과 동일해야 함.
        //   예전에는 organizationList[0]만 써서, 목록 정렬상 첫 행이 실제 소속(예: 뉴베이스 id=1)과 다르면 POST와 조회 불일치·403 유발 가능.
        // - 기관클래스(어드민): 드롭다운에서 선택한 기관 ID
        // - 오픈클래스: organizationId 필드 자체를 전송하지 않음
        let orgId: number | undefined;
        if (!isOpenClassPath) {
            orgId = isMaster
                ? (() => {
                      const fromState = Number(organizationId);
                      if (Number.isFinite(fromState) && fromState > 0) return Math.floor(fromState);
                      const first = Number(organizationList[0]?.id);
                      return Number.isFinite(first) && first > 0 ? first : 0;
                  })()
                : Number(organizationId) || 0;
        }

        // 파트너 기관 ID (오픈클래스일 때만 전송)
        const partnerOrganizationId = isOpenClassPath && partnerOrgId
            ? Number(partnerOrgId) || null
            : null;

        // classItemList 구성 (시나리오 + 강의 모두 포함)
        const classItemList = curriculumItems.map((item, idx) => {
            if (item.type === 'scenario') {
                const d = item.data as ScenarioDetail & { scenarioId?: number };
                const sid = d.scenarioId ?? d.id ?? 0;
                return { type: 'SCENARIO' as const, order: idx, scenarioId: sid };
            } else {
                const v = item.data as unknown as { id?: string; title?: string; url?: string; author?: { name?: string }; description?: string };
                return {
                    type: 'LECTURE' as const,
                    order: idx,
                    lectureTranslations: [
                        {
                            language: 'ko',
                            title: v.title ?? '',
                            description: v.description ?? '',
                        }
                    ],
                    lectureVideoUrl: v.url ?? '',
                    lectureCreatorName: v.author?.name ?? '',
                };
            }
        });

        const scenarioCount = classItemList.filter((i) => i.type === 'SCENARIO').length;
        const minCount = useCompletionRequirements
            ? (completionRequirements.minScenarioMode === 'all'
                ? scenarioCount
                : Math.min(completionRequirements.minScenarios, scenarioCount))
            : scenarioCount;

        const isOnline = participationMethod === 'online';
        const needsDates = !isOpenClassPath || !isOnline;
        const safeMaxParticipants = Math.max(1, Number.isFinite(maxParticipants) ? Math.floor(maxParticipants) : 1);
        const maxParticipantsForApi = hasUnlimitedOrderCapacity
            ? UNLIMITED_MAX_PARTICIPANTS
            : isOpenClassPath && isOnline
              ? 1
              : safeMaxParticipants;

        setIsSubmitting(true);
        try {
            const classI18nForApi = await withTranslationLoading(
                () => translateTitleDescTripleIfNeeded(classI18n, formLocale),
                { loadingMessage: '클래스 정보 번역 중...' }
            );
            if (process.env.NODE_ENV === 'development') {
                console.log('[ClassCreate translations]', classI18nForApi);
            }

            const payload = {
                // organizationId는 기관클래스일 때만 포함
                ...(orgId != null ? { organizationId: orgId } : {}),
                productId: selectedProductId,
                translations: buildClassTranslationTriple(coalesceEmptyTitleDescSlots(classI18nForApi)),
                ...(thumbnailImageUrl ? { thumbnailImageUrl } : {}),
                isOpenClass: isOpenClassPath,
                isOnlineClass: isOnline,
                productPriceId: effectiveProductPriceId,
                classItemList,
                minScenarioCompletionCount: Math.max(minCount, 1),
                minPassingScore: completionRequirements.minPassingScore,
                maxParticipants: maxParticipantsForApi,
                isActive,
                classStartDate: needsDates && startDate ? `${startDate}T00:00:00Z` : undefined,
                classEndDate: needsDates && endDate ? `${endDate}T23:59:59Z` : undefined,
                recruitmentStartDate: isOpenClassPath && !isOnline && recruitmentStartDate ? `${recruitmentStartDate}T00:00:00Z` : undefined,
                recruitmentEndDate: isOpenClassPath && !isOnline && recruitmentEndDate ? `${recruitmentEndDate}T23:59:59Z` : undefined,
                ...(resolvedOrderId != null && resolvedOrderId > 0 ? { orderId: resolvedOrderId } : {}),
                // partnerOrganizationId는 오픈클래스일 때만 포함
                ...(partnerOrganizationId != null ? { partnerOrganizationId } : {}),
            };

            await createMutation.mutateAsync(payload);
            showAlert(t('create.created'), true);
        } catch (e) {
            let msg = '클래스 생성에 실패했습니다.';
            if (e instanceof Error && e.message.includes('번역')) {
                msg = `자동 번역에 실패했습니다. ${e.message}`;
            } else if (e instanceof ApiError && e.status === 403) {
                msg = '기관에 속한 유저만 생성가능합니다.';
            } else if (e instanceof Error) {
                msg = e.message;
            }
            showAlert(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAlertClose = () => {
        setAlertOpen(false);
        if (navigateOnClose) goToClassList();
    };

    const handleCancel = () => {
        const hasContent =
            organizationId ||
            selectedProductId > 0 ||
            (['ko', 'en', 'ar', 'id', 'mn'] as const).some((c) => classI18n[c].title || classI18n[c].description || (classI18n[c].offlineClassDescription ?? '')) ||
            thumbnail ||
            curriculumItems.length > 0 ||
            partnerOrgId;
        if (hasContent && !window.confirm(t('create.confirmCancel'))) return;
        goToClassList();
    };

    const isAdminClassCreate = location.pathname.includes('/admin/class/create') || location.pathname.includes('/admin/open-class/create');

    return (
        <>
            <PageHeader
                title={pageTitle}
                breadcrumbs={isAdminClassCreate ? [] : [
                    { label: t('edit.breadcrumbManagement'), link: location.pathname.includes('/master/') ? ROUTES.MASTER.ORGANIZATION_CLASS_MANAGEMENT : ROUTES.ADMIN.ORGANIZATION_CLASS_MANAGEMENT },
                    { label: pageTitle },
                ]}
            />

            <div className="space-y-8 pb-12 max-w-4xl">
                <section className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="space-y-4">
                    {/* 파트너 선택 (오픈클래스만 표시) */}
                    {isOpenClassPath && (
                        <>
                            <div className="flex items-start gap-4">
                                <label className="text-sm font-medium text-gray-700 w-32 pt-2 shrink-0">
                                    {t('edit.selectPartner')}
                                </label>
                                <div className="flex-1">
                                    <Select
                                        value={partnerOrgId || 'none'}
                                        onValueChange={(v) => setPartnerOrgId(v === 'none' ? '' : v)}
                                        disabled={isPartnerLoading}
                                    >
                                        <SelectTrigger className="w-80">
                                            <SelectValue
                                                placeholder={
                                                    isPartnerLoading
                                                        ? '파트너 기관 조회 중...'
                                                        : t('edit.selectPartnerPlaceholder')
                                                }
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">{t('edit.partnerOrgNone')}</SelectItem>
                                            {partnerOrganizationList.map((org) => (
                                                <SelectItem key={org.id} value={String(org.id)}>
                                                    {org.title}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="border-t border-gray-200" />
                        </>
                    )}

                    {/* 고객사 선택 (기관클래스만, 어드민만 표시 / 마스터는 기관 1개로 자동 설정) */}
                    {!isMaster && !isOpenClassPath && (
                        <>
                            <div className="flex items-start gap-4">
                                <label className="text-sm font-medium text-gray-700 w-32 pt-2 shrink-0">
                                    {t('edit.selectCustomer')} <span className="text-red-500">*</span>
                                </label>
                                <div className="flex-1">
                                    <Select 
                                        value={organizationId} 
                                        onValueChange={(value) => {
                                            setOrganizationId(value);
                                            setOrganizationSearchText(''); // 선택 후 검색어 초기화
                                        }}
                                        onOpenChange={(open) => {
                                            if (!open) setOrganizationSearchText(''); // 닫힐 때 검색어 초기화
                                        }}
                                    >
                                        <SelectTrigger className="w-80">
                                            <SelectValue placeholder={t('edit.selectCustomerPlaceholder')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <div className="px-2 py-1.5 sticky top-0 bg-white border-b">
                                                <Input
                                                    type="text"
                                                    placeholder="기관명 검색..."
                                                    value={organizationSearchText}
                                                    onChange={(e) => setOrganizationSearchText(e.target.value)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onKeyDown={(e) => e.stopPropagation()}
                                                    className="h-8"
                                                />
                                            </div>
                                            <div className="max-h-[300px] overflow-y-auto">
                                                {filteredOrganizationList.length === 0 ? (
                                                    <div className="px-2 py-6 text-center text-sm text-gray-500">
                                                        검색 결과가 없습니다
                                                    </div>
                                                ) : (
                                                    filteredOrganizationList.map((org) => (
                                                        <SelectItem key={org.id} value={String(org.id)}>
                                                            {org.title}
                                                        </SelectItem>
                                                    ))
                                                )}
                                            </div>
                                        </SelectContent>
                                    </Select>
                                    {errors.organizationId && <p className="text-sm text-red-600 mt-1">{errors.organizationId}</p>}
                                </div>
                            </div>
                            <div className="border-t border-gray-200" />
                        </>
                    )}

                    {/* 프로덕트 선택 (실제 API 기반) */}
                    <div className="flex items-start gap-4">
                        <label className="text-sm font-medium text-gray-700 w-32 pt-2 shrink-0">{t('edit.productSelect')}</label>
                        <div className="flex-1">
                            <Select
                                value={
                                    isOrgClassCreatePath
                                        ? selectedProductPriceId > 0 ? String(selectedProductPriceId) : ''
                                        : selectedProductId > 0 ? String(selectedProductId) : ''
                                }
                                onValueChange={(v) => {
                                    const numVal = Number(v);
                                    if (isOrgClassCreatePath) {
                                        setSelectedProductPriceId(numVal);
                                    } else {
                                        setSelectedProductId(numVal);
                                    }
                                }}
                                disabled={!isOpenClassPath && !organizationId}
                            >
                                <SelectTrigger className="w-80">
                                    <SelectValue
                                        placeholder={
                                            !isOpenClassPath && !organizationId
                                                ? t('edit.productPlaceholderOrgFirst')
                                                : usePurchasedProductsForClass &&
                                                    organizationId &&
                                                    purchasedProductsFetching
                                                  ? t('create.productPlaceholderPurchasedLoading')
                                                  : (isOrgClassCreatePath ? purchasedList.length : productOptions.length) === 0
                                                    ? usePurchasedProductsForClass
                                                        ? t('edit.productPlaceholderNoPurchase')
                                                        : t('create.productPlaceholderNoProducts')
                                                    : t('create.productPlaceholderHasProducts')
                                        }
                                    />
                                </SelectTrigger>
                                <SelectContent>
                                    {isOrgClassCreatePath ? (
                                        purchasedList
                                            .filter(r => 
                                                isInstitutionClassProductSelectType(r.type || 'CONTENT')
                                            )
                                            .map((r) => {
                                                const title = r.productTitle?.trim() || '';
                                                const priceTitle = r.productPriceTitle?.trim() || '';
                                                const displayTitle = priceTitle ? `${title} (${priceTitle})` : title;
                                                return (
                                                    <SelectItem key={r.productPriceId} value={String(r.productPriceId)}>
                                                        {displayTitle}
                                                    </SelectItem>
                                                );
                                            })
                                    ) : (
                                        productOptions.map((p) => (
                                            <SelectItem key={p.productId} value={String(p.productId)}>
                                                {p.title}
                                            </SelectItem>
                                        ))
                                    )}
                                </SelectContent>
                                </Select>
                            {errors.productId && <p className="text-sm text-red-600 mt-1">{errors.productId}</p>}
                            {usePurchasedProductsForClass && selectedProductPriceId > 0 && (
                                <div className="mt-3 space-y-3">
                                    {purchasedProductsFetching ? (
                                        <p className="text-sm text-gray-500">{t('edit.salesUnitListLoading')}</p>
                                    ) : null}
                                    {errors.purchaseProduct && (
                                        <p className="text-sm text-red-600">{errors.purchaseProduct}</p>
                                    )}
                                    {resolvedOrderId && orderUserCountData !== undefined && (
                                        <div className="flex items-center gap-2 text-sm">
                                            <span className="text-gray-500">등록 가능 사용자 수:</span>
                                            {resolvedAvailableCapacity == null ? (
                                                <span className="font-semibold text-brand-700">무제한</span>
                                            ) : (
                                                <>
                                                    <span className="font-semibold text-brand-700">
                                                        {resolvedAvailableCapacity}
                                                        {t('edit.peopleUnit')}
                                                    </span>
                                                    {orderUserCountData.totalQuantity != null && (
                                                        <span className="text-gray-400">
                                                            (전체 {orderUserCountData.totalQuantity}
                                                            {t('edit.peopleUnit')} / 사용{' '}
                                                            {orderUserCountData.usedCapacity ?? 0}
                                                            {t('edit.peopleUnit')})
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 프로덕트 선택 시: 프로덕트 수정과 동일한 입력 필드 (수정 가능) */}
                    {selectedProductId > 0 && (
                        <>
                            <div className="flex items-start gap-4">
                                <label className="text-sm font-medium text-gray-700 w-32 pt-2 shrink-0">
                                    {t('edit.className')} / {t('edit.description')} <span className="text-red-500">*</span>
                                </label>
                                <div className="flex-1 min-w-0 w-full max-w-2xl space-y-2">
                                    <Input
                                        type="text"
                                        value={classI18n[formLocale].title}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            setClassI18n((prev) => ({
                                                ...prev,
                                                [formLocale]: { ...prev[formLocale], title: v },
                                            }));
                                        }}
                                        placeholder={t('edit.classNamePlaceholder')}
                                    />
                                    <Input
                                        multiline
                                        value={classI18n[formLocale].description}
                                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                            const v = e.target.value;
                                            setClassI18n((prev) => ({
                                                ...prev,
                                                [formLocale]: { ...prev[formLocale], description: v },
                                            }));
                                        }}
                                        placeholder={t('edit.descriptionPlaceholder')}
                                        rows={4}
                                    />
                                    {errors.className && <p className="text-sm text-red-600 mt-1">{errors.className}</p>}
                                </div>
                            </div>
                            {/* 썸네일 첨부 */}
                            <div className="flex items-start gap-4">
                                <label className="text-sm font-medium text-gray-700 w-32 pt-2 shrink-0">썸네일</label>
                                <div className="flex-1 space-y-3">
                                    <label className="flex items-center gap-2 cursor-pointer px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 w-fit">
                                        <Upload size={16} className="text-gray-600" />
                                        <span className="text-sm text-gray-700">이미지 파일 선택</span>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    setUploadedFile(file);
                                                    const reader = new FileReader();
                                                    reader.onload = (event) => {
                                                        setThumbnail(event.target?.result as string);
                                                    };
                                                    reader.readAsDataURL(file);
                                                }
                                            }}
                                            className="hidden"
                                        />
                                    </label>
                                    {uploadedFile && (
                                        <p className="text-sm text-gray-600">선택된 파일: {uploadedFile.name}</p>
                                    )}

                                    {/* 썸네일 미리보기 */}
                                    {thumbnail && (
                                        <div className="mt-3">
                                            <p className="text-sm text-gray-600 mb-2">미리보기:</p>
                                            <div className="w-32 h-32 rounded-lg border border-gray-200 overflow-hidden bg-gray-100">
                                                <img src={thumbnail} alt="Thumbnail preview" className="w-full h-full object-cover" />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <label className="text-sm font-medium text-gray-700 w-32 pt-2 shrink-0">{t('shared.curriculum')}</label>
                                <div className="flex-1 space-y-3">
                                    {curriculumItems.length === 0 ? (
                                        <Button type="button" variant="outline" size="sm" onClick={() => setScenarioModalOpen(true)}><Plus size={14} className="mr-1" />{t('create.addCurriculum')}</Button>
                                    ) : null}
                                    {curriculumItems.length > 0 && (
                                        <CurriculumSetup items={curriculumItems} onItemsChange={setCurriculumItems} error={errors.scenarios} hideEditCompleteAndReset />
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <label className="text-sm font-medium text-gray-700 w-32 pt-2 shrink-0">{t('edit.completionRequirements')}</label>
                                <div className="flex-1 space-y-3">
                                    {!useCompletionRequirements ? (
                                        <Button type="button" variant="outline" size="sm" onClick={() => setUseCompletionRequirements(true)}><Plus size={14} className="mr-1" />{t('create.addCompletionRequirements')}</Button>
                                    ) : (
                                        <div>
                                            <div className="space-y-3">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm text-gray-600 w-28">{t('create.minScenariosLabel')}</span>
                                                    <label className="flex items-center gap-1.5 cursor-pointer">
                                                        <input type="radio" name="minScenarioMode" checked={completionRequirements.minScenarioMode === 'all'} onChange={() => setCompletionRequirements({ ...completionRequirements, minScenarioMode: 'all' })} className="w-4 h-4 text-blue-600 border-gray-300" />
                                                        <span className="text-sm text-gray-700">{t('create.all')}</span>
                                                    </label>
                                                    <label className="flex items-center gap-1.5 cursor-pointer">
                                                        <input type="radio" name="minScenarioMode" checked={completionRequirements.minScenarioMode === 'custom'} onChange={() => setCompletionRequirements({ ...completionRequirements, minScenarioMode: 'custom' })} className="w-4 h-4 text-blue-600 border-gray-300" />
                                                        <span className="text-sm text-gray-700">{t('create.customInput')}</span>
                                                    </label>
                                                    {completionRequirements.minScenarioMode === 'custom' && (
                                                        <span className="flex items-center gap-2">
                                                            <Input type="number" min={1} max={curriculumItems.length || 1} value={completionRequirements.minScenarios} onChange={(e) => setCompletionRequirements({ ...completionRequirements, minScenarios: parseInt(e.target.value, 10) || 1 })} className="w-20" />
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm text-gray-600 w-28">{t('create.minPassingScoreLabel')}</span>
                                                    <Input type="number" min={0} max={100} value={completionRequirements.minPassingScore} onChange={(e) => setCompletionRequirements({ ...completionRequirements, minPassingScore: parseInt(e.target.value, 10) || 0 })} className="w-20" />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {/* 오픈클래스·어드민 기관클래스(비구매 경로): 판매단위 선택 — 마스터는 UI 없이 자동 선택 */}
                    {selectedProductId > 0 && !isAdminOrgClassCreate && (
                        <>
                            <div className="border-t border-gray-200" />
                            {!isMaster && (
                                <div className="flex items-center gap-4">
                                    <label className="text-sm font-medium text-gray-700 w-32 pt-2 shrink-0">{t('create.salesUnitSelect')}</label>
                                    <div className="flex-1 space-y-3">
                                        <Select
                                            value={selectedSalesUnitId > 0 ? String(selectedSalesUnitId) : ''}
                                            onValueChange={(v) => setSelectedSalesUnitId(Number(v))}
                                        >
                                            <SelectTrigger className="w-80">
                                                <SelectValue placeholder={t('edit.salesUnitPlaceholder')} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {priceOptions.map((price) => (
                                                    <SelectItem key={price.productPriceId} value={String(price.productPriceId)}>
                                                        {price.title} ({price.skuCode})
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {errors.salesUnit && <p className="text-sm text-red-600 mt-1">{errors.salesUnit}</p>}
                                        {productDetailLoading && (
                                            <p className="text-sm text-gray-500">{t('edit.salesUnitListLoading')}</p>
                                        )}
                                        {selectedSalesUnitId > 0 && (() => {
                                            const price = priceOptions.find((p) => p.productPriceId === selectedSalesUnitId);
                                            if (!price) return null;
                                            const periodLabel = price.validityPeriod == null
                                                ? t('create.unlimited')
                                                : `${price.validityPeriod}${price.validityPeriodUnit === 'DAY' ? t('create.periodDay') : price.validityPeriodUnit === 'MONTH' ? t('create.periodMonth') : t('create.periodYear')}`;
                                            return (
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm rounded-lg bg-gray-50 border border-gray-200 px-4 py-3">
                                                    <div className="flex"><span className="text-gray-500 w-24 shrink-0">{t('create.salesUnitName')}</span><span className="text-gray-900 font-medium">{price.title}</span></div>
                                                    <div className="flex"><span className="text-gray-500 w-24 shrink-0">{t('create.skuCode')}</span><span className="text-gray-900 font-mono">{price.skuCode}</span></div>
                                                    <div className="flex"><span className="text-gray-500 w-24 shrink-0">{t('create.usagePeriod')}</span><span className="text-gray-900">{periodLabel}</span></div>
                                                    <div className="flex"><span className="text-gray-500 w-24 shrink-0">{t('create.listPrice')}</span><span className="text-gray-900">{formatSalesUnitMoneyCell(price.regularPrice, price.currency)}</span></div>
                                                    <div className="flex"><span className="text-gray-500 w-24 shrink-0">{t('create.discountPrice')}</span><span className="text-gray-900 font-medium">{formatSalesUnitMoneyCell(price.discountedPrice, price.currency)}</span></div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            )}
                            {isMaster && errors.salesUnit && (
                                <p className="text-sm text-red-600 mt-2 px-1">{errors.salesUnit}</p>
                            )}
                            <div className="border-t border-gray-200" />
                        </>
                    )}

                    {/* 참여방법 (오픈클래스만 표시) */}
                    {isOpenClassPath && (
                        <div className="flex items-center gap-4">
                            <label className="text-sm font-medium text-gray-700 w-32 shrink-0">{t('edit.participationMethod')}</label>
                            <div className="flex-1 flex gap-6">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="participationMethod"
                                        checked={participationMethod === 'online'}
                                        onChange={() => setParticipationMethod('online')}
                                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700">{t('edit.online')}</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="participationMethod"
                                        checked={participationMethod === 'inPerson'}
                                        onChange={() => setParticipationMethod('inPerson')}
                                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700">{t('edit.inPerson')}</span>
                                </label>
                            </div>
                        </div>
                    )}

                    {/* 수강기간: 기관클래스 항상 표시 / 오픈클래스는 대면만 표시 (온라인은 기간 제한 없음) */}
                    {selectedProductId > 0 && (!isOpenClassPath || participationMethod === 'inPerson') && (
                        <>
                            <div className="flex items-center gap-4">
                                <label className="text-sm font-medium text-gray-700 w-32 pt-2 shrink-0">{t('edit.participationPeriod')} <span className="text-red-500">*</span></label>
                                <div className="flex-1 flex gap-4">
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1">{t('edit.startDate')}</label>
                                        <Input 
                                            type="date" 
                                            value={startDate} 
                                            onChange={(e) => setStartDate(e.target.value)} 
                                            min={productStartAt || undefined}
                                            className="w-40" 
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1">{t('edit.endDate')}</label>
                                        <Input 
                                            type="date" 
                                            value={endDate} 
                                            onChange={(e) => setEndDate(e.target.value)} 
                                            min={todayYmd} 
                                            max={productExpiresAt || undefined}
                                            className="w-40" 
                                        />
                                        {errors.endDate && <p className="text-sm text-red-600 mt-1">{errors.endDate}</p>}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* 대면 시: 모집기간, 참여 인원, 참여안내 */}
                    {isOpenClassPath && participationMethod === 'inPerson' && (
                        <>
                            <div className="flex items-center gap-4">
                                <label className="text-sm font-medium text-gray-700 w-32 shrink-0">{t('create.recruitmentPeriod')}</label>
                                <div className="flex-1 flex gap-4">
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1">{t('edit.startDate')}</label>
                                        <Input
                                            type="date"
                                            value={recruitmentStartDate}
                                            onChange={(e) => setRecruitmentStartDate(e.target.value)}
                                            className="w-40"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1">{t('edit.endDate')}</label>
                                        <Input 
                                            type="date" 
                                            value={recruitmentEndDate} 
                                            onChange={(e) => setRecruitmentEndDate(e.target.value)} 
                                            min={recruitmentStartDate || undefined}
                                            max={maxRecruitmentEndDate}
                                            className="w-40" 
                                        />
                                        {errors.recruitmentEndDate && <p className="text-sm text-red-600 mt-1">{errors.recruitmentEndDate}</p>}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <label className="text-sm font-medium text-gray-700 w-32 shrink-0">{t('create.participantsLabel')}</label>
                                <div className="flex-1 flex items-center gap-2">
                                    {hasUnlimitedOrderCapacity ? (
                                        <span className="inline-flex h-10 items-center rounded-md border border-gray-200 bg-gray-50 px-3 text-sm font-semibold text-brand-700">
                                            무제한
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1">
                                            <Input
                                                type="number"
                                                min={1}
                                                max={resolvedAvailableCapacity ?? undefined}
                                                value={maxParticipants}
                                                onChange={(e) => setMaxParticipants(clampParticipantsInput(e.target.value, resolvedAvailableCapacity))}
                                                className="w-24 text-right"
                                            />
                                            <span className="text-sm text-gray-600">{t('edit.peopleUnit')}</span>
                                        </span>
                                    )}
                                    {resolvedAvailableCapacity != null && (
                                        <span className="text-sm text-gray-500">
                                            (가능인원: {resolvedAvailableCapacity}{t('edit.peopleUnit')})
                                        </span>
                                    )}
                                    {errors.maxParticipants && <p className="text-sm text-red-600 ml-2">{errors.maxParticipants}</p>}
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <label className="text-sm font-medium text-gray-700 w-32 shrink-0 pt-2">
                                    {t('create.participationGuide')}
                                </label>
                                <div className="flex-1 min-w-0">
                                    <Input
                                        multiline
                                        value={classI18n[formLocale].offlineClassDescription}
                                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                            const v = e.target.value;
                                            setClassI18n((prev) => ({
                                                ...prev,
                                                [formLocale]: { ...prev[formLocale], offlineClassDescription: v },
                                            }));
                                        }}
                                        placeholder={t('create.participationGuidePlaceholder')}
                                        rows={4}
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    {/* 참가인원 (기관클래스만 표시) */}
                    {!isOpenClassPath && (
                        <>
                            <div className="border-t border-gray-200" />
                            <div className="flex items-center gap-4">
                                <label className="text-sm font-medium text-gray-700 w-32 shrink-0">{t('edit.participants')}</label>
                                <div className="flex-1 flex items-center gap-2">
                                    {hasUnlimitedOrderCapacity ? (
                                        <span className="inline-flex h-10 items-center rounded-md border border-gray-200 bg-gray-50 px-3 text-sm font-semibold text-brand-700">
                                            무제한
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1">
                                            <Input
                                                type="number"
                                                min={1}
                                                max={resolvedAvailableCapacity ?? undefined}
                                                value={maxParticipants}
                                                onChange={(e) => setMaxParticipants(clampParticipantsInput(e.target.value, resolvedAvailableCapacity))}
                                                className="w-24 text-left"
                                            />
                                            <span className="text-sm text-gray-600">{t('edit.peopleUnit')}</span>
                                        </span>
                                    )}
                                    {orderUserCountData !== undefined && (
                                        <span className="text-sm text-gray-500">
                                            {resolvedAvailableCapacity == null
                                                ? `(${t('create.registrableUsers')}: 무제한)`
                                                : `(${t('create.registrableUsers')}: ${resolvedAvailableCapacity}${t('edit.peopleUnit')})`}
                                        </span>
                                    )}
                                    {errors.maxParticipants && <p className="text-sm text-red-600 ml-2">{errors.maxParticipants}</p>}
                                </div>
                            </div>
                        </>
                    )}

                    {/* 활성 상태 */}
                    <div className="border-t border-gray-200" />
                    <div className="flex items-center gap-4">
                        <label className="text-sm font-medium text-gray-700 w-32 shrink-0">{t('edit.activeStatus')}</label>
                        <div className="flex-1">
                            <ToggleLabel
                                checked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                                onLabel={t('edit.active')}
                                offLabel={t('edit.inactive')}
                                size="sm"
                            />
                        </div>
                    </div>
                    </div>
                </section>

                <AddScenarioModal
                    isOpen={scenarioModalOpen}
                    onClose={() => setScenarioModalOpen(false)}
                    onSelect={handleAddScenariosFromModal}
                    selectedScenarioIds={selectedScenarioIds}
                />


                <div className="flex justify-end gap-3">
                    <Button variant="secondary" onClick={handleCancel} disabled={isSubmitting}>
                        {t('edit.cancel')}
                    </Button>
                    <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting || createMutation.isPending}>
                        {isSubmitting ? t('create.submitting') : t('create.submit')}
                    </Button>
                </div>
            </div>

            <AlertDialog isOpen={alertOpen} onClose={handleAlertClose} message={alertMsg} />
        </>
    );
}
